import type { ReactNode } from 'react';
import { cn } from '../../cn.js';

export interface ChartTooltipProps {
  /** Horizontal position as a percentage (0-100) of the chart's rendered width. */
  x: number;
  /** Vertical position as a percentage (0-100) of the chart's rendered height. */
  y: number;
  children: ReactNode;
  className?: string;
}

/**
 * Floating value readout for a hovered or keyboard-focused chart point/bar. Plain HTML, not SVG
 * `<text>` — SVG text can't wrap, can't use this package's floating-surface tokens
 * (`bg-surface-raised` + `shadow-lg`, the same pair `DropdownMenu.tsx`'s popover uses), and would
 * clip against the chart's own `viewBox` at the edges instead of overflowing the page naturally.
 *
 * Positioned by percentage rather than pixels because the chart's `<svg>` is `width="100%"` —
 * it has no fixed pixel size for this component to convert against, but percentage-of-container
 * is exactly what `viewBox` coordinates already are once divided by the chart's own view width
 * and height. The caller's chart wraps both its `<svg>` and this tooltip in one `relative` element.
 */
export function ChartTooltip({ x, y, children, className }: ChartTooltipProps) {
  return (
    <div
      role="tooltip"
      className={cn(
        'pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap',
        'rounded-md border border-border bg-surface-raised px-2 py-1 text-xs text-fg shadow-lg',
        className,
      )}
      style={{ left: `${x}%`, top: `${y}%`, marginTop: -8 }}
    >
      {children}
    </div>
  );
}
